import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { courses } from '@/mocks/courses';
import { categories, zones, getCategoryBySlug, getZoneBySlug } from '@/mocks/categories';

const courseImagesForCategory: Record<string, string> = {
  'jp-lang-001': 'https://readdy.ai/api/search-image?query=Japanese%20language%20learning%20concept%20minimalist%20design%2C%20warm%20lighting%2C%20clean%20background%2C%20professional%20atmosphere&width=600&height=400&seq=cat-jp-lang-001&orientation=landscape',
  'jp-career-001': 'https://readdy.ai/api/search-image?query=Japanese%20business%20office%20minimalist%20design%2C%20warm%20lighting%2C%20professional%20atmosphere&width=600&height=400&seq=cat-jp-career-001&orientation=landscape',
  'jp-culture-001': 'https://readdy.ai/api/search-image?query=Traditional%20Japanese%20tea%20ceremony%20minimalist%2C%20warm%20natural%20lighting%2C%20serene%20tatami%20room&width=600&height=400&seq=cat-jp-culture-001&orientation=landscape',
  'kr-lang-001': 'https://readdy.ai/api/search-image?query=Korean%20language%20learning%20minimalist%20design%2C%20warm%20lighting%2C%20clean%20background%2C%20professional&width=600&height=400&seq=cat-kr-lang-001&orientation=landscape',
  'kr-career-001': 'https://readdy.ai/api/search-image?query=Korean%20business%20office%20minimalist%20design%2C%20modern%20workspace%2C%20warm%20lighting&width=600&height=400&seq=cat-kr-career-001&orientation=landscape',
  'kr-culture-001': 'https://readdy.ai/api/search-image?query=Korean%20pop%20culture%20and%20media%20minimalist%20design%2C%20creative%20atmosphere%2C%20warm%20lighting&width=600&height=400&seq=cat-kr-culture-001&orientation=landscape',
  'jp-degree-001': 'https://readdy.ai/api/search-image?query=Japanese%20university%20campus%20minimalist%20design%2C%20cherry%20blossoms%2C%20warm%20lighting%2C%20academic%20atmosphere&width=600&height=400&seq=cat-jp-degree-001&orientation=landscape',
  'kr-degree-001': 'https://readdy.ai/api/search-image?query=Korean%20university%20campus%20minimalist%20design%2C%20modern%20architecture%2C%20warm%20lighting%2C%20academic%20atmosphere&width=600&height=400&seq=cat-kr-degree-001&orientation=landscape',
  'jp-exam-001': 'https://readdy.ai/api/search-image?query=Japanese%20exam%20preparation%20minimalist%20design%2C%20study%20atmosphere%2C%20warm%20lighting%2C%20focused%20environment&width=600&height=400&seq=cat-jp-exam-001&orientation=landscape',
  'kr-exam-001': 'https://readdy.ai/api/search-image?query=Korean%20exam%20preparation%20minimalist%20design%2C%20study%20atmosphere%2C%20warm%20lighting%2C%20focused%20environment&width=600&height=400&seq=cat-kr-exam-001&orientation=landscape',
  'jp-lang-002': 'https://readdy.ai/api/search-image?query=Japan%20travel%20minimalist%20design%2C%20warm%20lighting%2C%20scenic%20atmosphere%2C%20clean%20aesthetic&width=600&height=400&seq=cat-jp-lang-002&orientation=landscape',
  'kr-lang-002': 'https://readdy.ai/api/search-image?query=Korean%20drama%20and%20film%20minimalist%20design%2C%20warm%20lighting%2C%20creative%20atmosphere&width=600&height=400&seq=cat-kr-lang-002&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<string>('all');
  const [activeZone, setActiveZone] = useState<string>('all');

  const isZonePage = slug && slug.startsWith('zone-');
  const zoneInfo = isZonePage ? getZoneBySlug(slug) : undefined;
  const categoryInfo = !isZonePage ? getCategoryBySlug(slug || '') : undefined;

  const filteredCourses = useMemo(() => {
    let result = courses;

    if (isZonePage && zoneInfo) {
      const zoneLabel = zoneInfo.slug === 'zone-jp' ? '一区' : '二区';
      result = result.filter(c => c.zone === zoneLabel);
    }

    if (!isZonePage && categoryInfo) {
      result = result.filter(c => c.categorySlug === categoryInfo.slug);
    }

    if (activeTab !== 'all' && activeTab !== 'zone') {
      result = result.filter(c => c.categorySlug === activeTab);
    }

    if (activeZone !== 'all' && activeZone !== 'cat') {
      const zoneLabel = activeZone === 'zone-jp' ? '一区' : '二区';
      result = result.filter(c => c.zone === zoneLabel);
    }

    return result;
  }, [slug, activeTab, activeZone, isZonePage, zoneInfo, categoryInfo]);

  const pageTitle = zoneInfo
    ? `${zoneInfo.label}课程`
    : categoryInfo
      ? `${categoryInfo.name}课程`
      : '全部课程';

  const pageDescription = zoneInfo
    ? zoneInfo.description
    : categoryInfo
      ? categoryInfo.description
      : '';

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    ...(isZonePage ? [{ label: zoneInfo?.label || '' }] : []),
    ...(!isZonePage ? [{ label: categoryInfo?.name || '' }] : []),
  ];

  const zoneTabs = isZonePage
    ? [{ label: `全部${zoneInfo?.label || ''}`, value: 'all' }]
    : [
        { label: '全部', value: 'all' },
        { label: '日本区', value: 'zone-jp' },
        { label: '韩国区', value: 'zone-kr' },
      ];

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen bg-background-50">
        <div className="bg-background-100 pt-24 pb-8">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mt-4 mb-2">
              {pageTitle}
            </h1>
            {pageDescription && (
              <p className="text-foreground-600 text-sm max-w-2xl">{pageDescription}</p>
            )}
          </div>
        </div>

        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
            {!isZonePage && (
              <div className="flex items-center gap-2 bg-background-100 rounded-full px-1 py-1">
                {zoneTabs.map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => {
                      setActiveZone(tab.value);
                      setActiveTab('all');
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      activeZone === tab.value
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveTab(activeTab === cat.slug ? 'all' : cat.slug)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeTab === cat.slug
                      ? 'bg-secondary-200 text-secondary-800'
                      : 'bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {filteredCourses.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-inbox-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-base">暂无符合条件的课程</p>
              <button
                onClick={() => { setActiveTab('all'); setActiveZone('all'); }}
                className="mt-4 px-6 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                重置筛选
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
              {filteredCourses.map((course) => (
                <Link
                  key={course.id}
                  to={`/article/${course.id}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200 transition-all duration-300 hover:scale-[1.02]"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={courseImagesForCategory[course.id] || 'https://readdy.ai/api/search-image?query=education%20minimalist%20design%2C%20warm%20lighting%2C%20clean%20background%2C%20professional%20atmosphere&width=600&height=400&seq=cat-fallback&orientation=landscape'}
                      alt={course.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3 flex gap-2">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        course.zone === '一区'
                          ? 'bg-red-500/90 text-white'
                          : 'bg-amber-500/90 text-white'
                      }`}>
                        {course.zoneLabel}
                      </span>
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-background-50/90 text-foreground-700">
                        {course.level}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-xs text-primary-600 font-medium bg-primary-50 px-2 py-0.5 rounded">
                        {course.category}
                      </span>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {course.title}
                    </h3>
                    <p className="text-sm text-foreground-500 mb-4 line-clamp-2">
                      {course.shortDescription}
                    </p>
                    <div className="flex items-center gap-3 mb-4 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line"></i> {course.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i> {course.readTime}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t border-background-200">
                      <div className="flex items-center gap-1">
                        <i className="ri-star-fill text-amber-400 text-sm"></i>
                        <span className="text-sm font-semibold text-foreground-900">{course.rating}</span>
                        <span className="text-xs text-foreground-500">({course.students.toLocaleString()})</span>
                      </div>
                      <span className="text-base font-bold text-primary-600">{course.price}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}